import * as signalR from '@microsoft/signalr'
import { tokenStorage } from './tokenStorage'
import type { FlowClue, MasterRouteConfig, RouteNodePointConfig } from '@features/adventures/types/workflow.types'

// Empty means same-origin (dev, via the vite /hub proxy). Production sets the API origin.
const SIGNALR_URL = import.meta.env.VITE_SIGNALR_URL || ''

/**
 * Read the token when the hub asks, not when the connection was built — a reconnect that
 * re-presents the token from an hour ago authenticates with an expired credential and never comes
 * back. See signalr.service.ts for the longer version.
 */
const currentToken = (fallback: string) => () => tokenStorage.getAccessToken() || fallback

// Types for FlowHub
export interface FlowChange {
  id: string
  flowId: string
  userId: string
  userName: string
  changeData: FlowChangeData
  timestamp: string
}

export interface FlowParticipant {
  connectionId: string
  userId: string
  userName: string
  joinedAt: string
}

// Change data types - structured for the workflow editor and adventure editor
export type FlowChangeData =
  // Workflow/Flow changes
  | { type: 'node_add'; node: NodeAddData }
  | { type: 'node_move'; nodeId: string; position: { x: number; y: number } }
  | { type: 'node_delete'; nodeId: string }
  | { type: 'node_update'; nodeId: string; data: Record<string, unknown> }
  | { type: 'edge_add'; edge: EdgeAddData }
  | { type: 'edge_delete'; edgeId: string }
  | { type: 'edge_update'; edgeId: string; data: Record<string, unknown> }
  | { type: 'bulk_change'; changes: FlowChangeData[] }
  // clues is optional so a replace from a client that predates versioned clues leaves ours alone.
  | { type: 'workflow_replace'; nodes: NodeAddData[]; edges: EdgeAddData[]; clues?: FlowClue[]; masterRoute?: MasterRouteConfig | null; changeSequence?: number }
  // The route and the clue list, each whole, as saved by the editor who changed them.
  | { type: 'route_update'; masterRoute: MasterRouteConfig | null }
  | { type: 'clues_update'; clues: FlowClue[] }
  // The route editor's lock (store/routeLock) and the holder's unsaved working copy, for watchers.
  | { type: 'route_lock'; holderId: string; holderName: string; claimedAt: number }
  | { type: 'route_unlock'; holderId: string }
  | { type: 'route_draft'; holderId: string; sections: MasterRouteConfig['sections']; nodePoints: RouteNodePointConfig[] }
  // Adventure-level changes (for real-time collaboration across all editor panels)
  | { type: 'adventure_field_update'; field: string; value: unknown }
  | { type: 'section_focus'; section: string; userId: string; userName: string }
  | { type: 'section_blur'; section: string; userId: string }
  // Criteria changes
  | { type: 'criteria_add'; criteriaType: 'display' | 'join'; item: CriteriaItemData }
  | { type: 'criteria_update'; criteriaType: 'display' | 'join'; itemId: string; config: Record<string, unknown> }
  | { type: 'criteria_delete'; criteriaType: 'display' | 'join'; itemId: string }
  | { type: 'criteria_logic_group_add'; criteriaType: 'display' | 'join'; group: CriteriaLogicGroupData }
  | { type: 'criteria_logic_group_update'; criteriaType: 'display' | 'join'; groupId: string; operator: 'AND' | 'OR' }
  | { type: 'criteria_logic_group_delete'; criteriaType: 'display' | 'join'; groupId: string }

export interface CriteriaItemData {
  id: string
  conditionType: number
  config: Record<string, unknown>
  logicGroupId?: string
  order: number
}

export interface CriteriaLogicGroupData {
  id: string
  operator: 'AND' | 'OR'
  order: number
}

export interface NodeAddData {
  id: string
  type: string
  position: { x: number; y: number }
  data: Record<string, unknown>
}

export interface EdgeAddData {
  id: string
  source: string
  target: string
  sourceHandle?: string
  targetHandle?: string
  data?: Record<string, unknown>
}

// Event callbacks
export interface FlowHubCallbacks {
  onUserJoined?: (data: { flowId: string; userId: string; userName: string; timestamp: string }) => void
  onUserLeft?: (data: { flowId: string; userId: string; userName: string; timestamp: string }) => void
  onFlowChange?: (change: FlowChange) => void
  onParticipants?: (data: { flowId: string; participants: FlowParticipant[] }) => void
  onSyncRequest?: (data: { flowId: string; requesterId: string; requesterName: string }) => void
  onError?: (message: string) => void
  onReconnecting?: (error?: Error) => void
  onReconnected?: (connectionId?: string) => void
  onDisconnected?: (error?: Error) => void
}

class FlowHubService {
  private connection: signalR.HubConnection | null = null
  private currentFlowId: string | null = null
  private callbacks: FlowHubCallbacks = {}
  private participants: FlowParticipant[] = []

  /**
   * Connect to the FlowHub
   */
  async connect(token: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.log('FlowHub already connected')
      return
    }

    const logLevel = import.meta.env.DEV ? signalR.LogLevel.Debug : signalR.LogLevel.Information

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${SIGNALR_URL}/hub/flow`, {
        accessTokenFactory: currentToken(token),
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          // Retry with exponential backoff up to 30 seconds
          if (retryContext.elapsedMilliseconds < 60000) {
            const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000)
            return delay + Math.random() * 1000 // Add jitter
          }
          return null // Stop retrying after 1 minute
        }
      })
      .configureLogging(logLevel)
      .build()

    this.registerEventHandlers()

    try {
      await this.connection.start()
      console.log('FlowHub Connected. Connection ID:', this.connection.connectionId)
    } catch (err) {
      console.error('FlowHub Connection Error:', err)
      throw err
    }
  }

  /**
   * Register all SignalR event handlers
   */
  private registerEventHandlers(): void {
    if (!this.connection) return

    // User joined the flow
    this.connection.on('UserJoinedFlow', (data: { flowId: string; userId: string; userName: string; timestamp: string }) => {
      console.log(`${data.userName} joined the flow`)
      this.callbacks.onUserJoined?.(data)
    })

    // User left the flow
    this.connection.on('UserLeftFlow', (data: { flowId: string; userId: string; userName: string; timestamp: string }) => {
      console.log(`${data.userName} left the flow`)
      // Remove from local participants list
      this.participants = this.participants.filter(p => p.userId !== data.userId)
      this.callbacks.onUserLeft?.(data)
    })

    // Received a flow change from another user
    this.connection.on('ReceiveFlowChange', (change: FlowChange) => {
      console.log('[FlowHub] Received change:', change.changeData.type, 'from', change.userName, change)
      this.callbacks.onFlowChange?.(change)
    })

    // Received participants list
    this.connection.on('FlowParticipants', (data: { flowId: string; participants: FlowParticipant[] }) => {
      console.log('Flow participants:', data.participants.length)
      this.participants = data.participants
      this.callbacks.onParticipants?.(data)
    })

    // Sync request from a new user
    this.connection.on('SyncRequest', (data: { flowId: string; requesterId: string; requesterName: string }) => {
      console.log(`${data.requesterName} is requesting workflow sync`)
      this.callbacks.onSyncRequest?.(data)
    })

    // Error from server
    this.connection.on('Error', (message: string) => {
      console.error('FlowHub error:', message)
      this.callbacks.onError?.(message)
    })

    // Connection state handlers
    this.connection.onreconnecting(error => {
      console.warn('FlowHub reconnecting...', error)
      this.callbacks.onReconnecting?.(error)
    })

    this.connection.onreconnected(connectionId => {
      console.log('FlowHub reconnected. Connection ID:', connectionId)
      // Rejoin the flow if we were in one
      if (this.currentFlowId) {
        this.joinFlow(this.currentFlowId).catch(err => {
          console.error('Failed to rejoin flow after reconnect:', err)
        })
      }
      this.callbacks.onReconnected?.(connectionId)
    })

    this.connection.onclose(error => {
      console.error('FlowHub connection closed:', error)
      this.callbacks.onDisconnected?.(error)
    })
  }

  /**
   * Set callback handlers
   */
  setCallbacks(callbacks: FlowHubCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks }
  }

  /**
   * Clear all callbacks
   */
  clearCallbacks(): void {
    this.callbacks = {}
  }

  /**
   * Join a flow editing session
   */
  async joinFlow(flowId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('FlowHub not connected')
    }

    // Leave current flow if in one
    if (this.currentFlowId && this.currentFlowId !== flowId) {
      await this.leaveFlow()
    }

    try {
      await this.connection.invoke('JoinFlow', flowId)
      this.currentFlowId = flowId
      console.log('Joined flow:', flowId)
    } catch (err) {
      console.error('Failed to join flow:', err)
      throw err
    }
  }

  /**
   * Leave the current flow editing session
   */
  async leaveFlow(): Promise<void> {
    if (!this.connection || !this.currentFlowId) return

    try {
      await this.connection.invoke('LeaveFlow', this.currentFlowId)
      console.log('Left flow:', this.currentFlowId)
      this.currentFlowId = null
      this.participants = []
    } catch (err) {
      console.error('Failed to leave flow:', err)
      // Clear state even if the call fails
      this.currentFlowId = null
      this.participants = []
    }
  }

  /**
   * Send a flow change to other editors
   */
  async sendChange(changeData: FlowChangeData): Promise<void> {
    if (!this.connection || !this.currentFlowId) {
      console.warn('Cannot send change: not connected to a flow')
      return
    }

    try {
      console.log('[FlowHub] Sending change:', changeData.type, changeData)
      await this.connection.invoke('SendFlowChange', this.currentFlowId, changeData)
      console.log('[FlowHub] Change sent successfully')
    } catch (err) {
      console.error('Failed to send flow change:', err)
      throw err
    }
  }

  /**
   * Request the list of current participants
   */
  async getParticipants(): Promise<void> {
    if (!this.connection || !this.currentFlowId) {
      console.warn('Cannot get participants: not connected to a flow')
      return
    }

    try {
      await this.connection.invoke('GetFlowParticipants', this.currentFlowId)
    } catch (err) {
      console.error('Failed to get participants:', err)
      throw err
    }
  }

  /**
   * Request sync from other participants (called when joining a flow with existing editors)
   */
  async requestSync(): Promise<void> {
    if (!this.connection || !this.currentFlowId) {
      console.warn('Cannot request sync: not connected to a flow')
      return
    }

    try {
      await this.connection.invoke('RequestSync', this.currentFlowId)
      console.log('Requested workflow sync from other participants')
    } catch (err) {
      console.error('Failed to request sync:', err)
      // Don't throw - sync request is best-effort
    }
  }

  /**
   * Get cached participants list
   */
  getCachedParticipants(): FlowParticipant[] {
    return [...this.participants]
  }

  /**
   * Get current flow ID
   */
  getCurrentFlowId(): string | null {
    return this.currentFlowId
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected
  }

  /**
   * Disconnect from FlowHub
   */
  async disconnect(): Promise<void> {
    await this.leaveFlow()

    if (this.connection) {
      try {
        await this.connection.stop()
        console.log('FlowHub Disconnected')
      } catch (err) {
        console.error('Error disconnecting from FlowHub:', err)
      }
      this.connection = null
    }
  }

  /**
   * Get the raw connection (for advanced use cases)
   */
  getConnection(): signalR.HubConnection | null {
    return this.connection
  }
}

// Export singleton instance
export const flowHubService = new FlowHubService()
